import { useState } from 'react';
import { Modal, Upload, Button, Typography, Space, Select, Input, App } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { AllowedMime } from '@bom/shared';
import { usePresignUpload, putFileToS3 } from '../../api/attachments';
import { useCreateSpec } from '../../api/specs';

const { Dragger } = Upload;

const EXT_MIME: Record<string, AllowedMime> = {
  pdf: 'application/pdf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  zip: 'application/zip',
  txt: 'text/plain',
  csv: 'text/csv',
};
function mimeByName(name: string): AllowedMime | null {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  return EXT_MIME[ext] ?? null;
}

interface Props {
  tid: string;
  open: boolean;
  onClose: () => void;
  /** BOM 料号选项：归属料号只能从中搜索选择（不在 BOM 中的料号不能归档规格书） */
  partOptions?: { value: string }[];
  defaultPartNo?: string;
}

export function SpecUploadModal({ tid, open, onClose, partOptions, defaultPartNo }: Props) {
  const { message } = App.useApp();
  const presign = usePresignUpload(tid);
  const create = useCreateSpec(tid);
  const [file, setFile] = useState<File | null>(null);
  const [partNos, setPartNos] = useState<string[]>(defaultPartNo ? [defaultPartNo] : []);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setFile(null);
    setPartNos(defaultPartNo ? [defaultPartNo] : []);
    setNote('');
  };

  const onOk = async () => {
    if (partNos.length === 0) return message.warning('请选择归属料号（可多选）');
    const invalid = partNos.filter((pn) => !(partOptions ?? []).some((o) => o.value === pn));
    if (invalid.length > 0)
      return message.warning(`归属料号必须从本产品 BOM 料号中选择：${invalid.join('、')}`);
    if (!file) return message.warning('请选择规格书文件');
    const ct = mimeByName(file.name);
    if (!ct) return message.error('不支持的文件类型（支持 PDF/Word/Excel/PPT/图片/zip/txt/csv）');
    setSubmitting(true);
    try {
      const pre = await presign.mutateAsync({
        filename: file.name,
        content_type: ct,
        size_bytes: file.size,
        purpose: 'general',
      });
      await putFileToS3(pre.upload_url, file, ct);
      await create.mutateAsync({
        part_nos: partNos,
        filename: file.name,
        s3_key: pre.s3_key,
        ...(note.trim() ? { note: note.trim() } : {}),
        size_bytes: file.size,
      });
      message.success('规格书已上传归档');
      reset();
      onClose();
    } catch (e) {
      message.error((e as Error).message || '上传失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="上传规格书"
      open={open}
      onOk={onOk}
      confirmLoading={submitting}
      onCancel={() => {
        reset();
        onClose();
      }}
      okText="上传归档"
      cancelText="取消"
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Typography.Text>归属料号（可多选，多个料号共用同一份规格书）</Typography.Text>
          <Select
            mode="multiple"
            style={{ width: '100%', marginTop: 4 }}
            value={partNos}
            onChange={(v) => setPartNos(v ?? [])}
            options={partOptions ?? []}
            placeholder={
              (partOptions ?? []).length > 0
                ? '搜索并选择本产品 BOM 中的料号（可多选）'
                : '本产品暂无 BOM 料号，请先上传 BOM'
            }
            disabled={(partOptions ?? []).length === 0}
            showSearch
            filterOption={(input, opt) =>
              String(opt?.value ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            maxTagCount="responsive"
            allowClear
          />
        </div>
        <div>
          <Typography.Text>版本说明（可选）</Typography.Text>
          <Input
            style={{ marginTop: 4 }}
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder="如：V2 更新耐压参数"
          />
        </div>
        <Dragger
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.zip,.txt,.csv"
          multiple={false}
          maxCount={1}
          showUploadList={false}
          beforeUpload={(f) => {
            setFile(f as unknown as File);
            return false; // 阻止自动上传，点「上传归档」才真正上传
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{file ? file.name : '点击或拖拽规格书文件到此处'}</p>
          <p className="ant-upload-hint">支持 PDF / Word / Excel / PPT / 图片 / zip / txt / csv</p>
        </Dragger>
        <Typography.Text type="secondary">
          规格书仅归档、不发送邮件；同一料号多次上传即形成更新时间线。可选多个料号共用同一份文件；
          规格书按料号跨产品共享 —— 其他产品的 BOM 含所选料号时也会显示。
        </Typography.Text>
      </Space>
    </Modal>
  );
}
